import { useCallback, useEffect, useMemo, useState } from 'react'
import { useI18n } from '../../i18n'
import { API_ENDPOINTS } from '../../config/api'
import { Button } from '../ui/Button'
import { Select } from '../ui/Select'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import styles from '../../styles/pages/Reports.module.css'
import ReportsEnterpriseSection from './ReportsEnterpriseSection'
import ScannerParsedReportView from './ScannerParsedReportView'
import {
  fetchParsedReport,
  downloadScannerReport,
  reconcileScannerScans,
  type ParsedReportPayload,
} from '../../services/scannerGatewayService'
import { formatScannerSourceLabel } from '../../services/vulnerabilityPostureApi'

type AssessmentRow = {
  id: string
  scannerScanId: string
  finishedAt?: string | null
  providerType?: string
  target?: string | null
  profile?: string | null
  status?: string
  parserStatus?: string | null
  quality?: string | null
  analyzedObjects?: number | null
  uniqueFindings?: number
  affectedInstances?: number
  rawOccurrences?: number
  newFindings?: number
  resolvedFindings?: number
  governsCurrentPosture?: boolean
  postureRole?: string | null
  shortId?: string
  assetId?: string | null
  previousScannerScanId?: string | null
  compareDelta?: {
    comparable?: boolean
    newFindings?: number
    resolvedFindings?: number
  } | null
}

type InventoryResponse = {
  items: AssessmentRow[]
  total: number
  summary: {
    totalAssessments: number
    completed: number
    running: number
    failed: number
    targetsAssessed: number
    latestAssessmentAt?: string | null
    assessmentsWithFindings: number
    zeroFindingAssessments: number
  }
}

type Props = {
  token: string
  initialScanId?: string | null
  assetId?: string | null
  onScanIdChange?: (scanId: string | null) => void
  /** Invalidate Overview / Current Findings after assessment materialization. */
  onPostureMaybeChanged?: () => void
}

function statusBadgeClass(status: string) {
  const s = String(status || '').toUpperCase()
  if (s === 'COMPLETED') return entStyles.badgeStable
  if (s === 'RUNNING' || s === 'QUEUED') return entStyles.badgeWarn
  if (s === 'FAILED') return entStyles.badgeRisk
  return entStyles.badge
}

function formatDay(value?: string | null) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

export default function ScannerAssessmentsWorkspace({
  token,
  initialScanId = null,
  assetId = null,
  onScanIdChange,
  onPostureMaybeChanged,
}: Props) {
  const { t } = useI18n()
  const [data, setData] = useState<InventoryResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(initialScanId)
  const [parsed, setParsed] = useState<ParsedReportPayload | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [providerFilter, setProviderFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [findingsFilter, setFindingsFilter] = useState<'all' | 'with' | 'zero'>('all')
  const [postureFilter, setPostureFilter] = useState<'all' | 'current' | 'historical'>('all')
  const [pageSize, setPageSize] = useState(25)
  const [page, setPage] = useState(1)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await reconcileScannerScans(token, { limit: 100 }).catch(() => undefined)
      const params = new URLSearchParams({ limit: '200' })
      if (assetId) params.set('assetId', assetId)
      if (providerFilter !== 'all') params.set('provider', providerFilter)
      if (statusFilter !== 'all') params.set('status', statusFilter)
      if (findingsFilter === 'with') params.set('hasFindings', 'true')
      if (findingsFilter === 'zero') params.set('hasFindings', 'zero')
      if (postureFilter === 'current') params.set('postureRole', 'CURRENT')
      if (postureFilter === 'historical') params.set('postureRole', 'HISTORICAL')
      const res = await fetch(`${API_ENDPOINTS.REPORTS_ASSESSMENTS}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.message || 'Failed to load assessments')
      setData(json as InventoryResponse)
      onPostureMaybeChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load assessments')
    } finally {
      setLoading(false)
    }
  }, [token, assetId, providerFilter, statusFilter, findingsFilter, postureFilter, onPostureMaybeChanged])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (initialScanId) setSelectedId(initialScanId)
  }, [initialScanId])

  const openParsed = useCallback(
    async (scanId: string) => {
      setSelectedId(scanId)
      onScanIdChange?.(scanId)
      setDetailLoading(true)
      setParsed(null)
      try {
        const payload = await fetchParsedReport(token, scanId)
        setParsed(payload)
        onPostureMaybeChanged?.()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to open assessment')
        setSelectedId(null)
        onScanIdChange?.(null)
      } finally {
        setDetailLoading(false)
      }
    },
    [token, onScanIdChange, onPostureMaybeChanged],
  )

  useEffect(() => {
    if (initialScanId) void openParsed(initialScanId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const items = useMemo(() => {
    const list = [...(data?.items || [])]
    list.sort((a, b) => {
      const tb = a.finishedAt ? new Date(a.finishedAt).getTime() : 0
      const ta = b.finishedAt ? new Date(b.finishedAt).getTime() : 0
      const nb = Number.isFinite(tb) ? tb : 0
      const na = Number.isFinite(ta) ? ta : 0
      // Newest assessments first
      if (na !== nb) return na - nb
      return String(b.id || '').localeCompare(String(a.id || ''))
    })
    return list
  }, [data?.items])
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize))
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize
    return items.slice(start, start + pageSize)
  }, [items, page, pageSize])

  useEffect(() => {
    setPage(1)
  }, [providerFilter, statusFilter, findingsFilter, postureFilter, pageSize, assetId])

  if (selectedId) {
    return (
      <ScannerParsedReportView
        loading={detailLoading}
        parsed={parsed}
        onBack={() => {
          setSelectedId(null)
          setParsed(null)
          onScanIdChange?.(null)
          void load()
          onPostureMaybeChanged?.()
        }}
        onDownload={(format) => {
          void downloadScannerReport(token, selectedId, format)
        }}
      />
    )
  }

  const summary = data?.summary

  return (
    <ReportsEnterpriseSection
      layerLabel={t('reports.assessments.layerLabel', 'Scanner assessments')}
      title={t('reports.assessments.title', 'Scanner Assessments')}
      subtitle={t(
        'reports.assessments.subtitle',
        'Authoritative inventory of every scanner execution. Replaces Scanner Activity, Recent Reports scanner rows, and Assets Completed Scans.',
      )}
      testId="scanner-assessments-workspace"
    >
      {error ? (
        <div className={styles.alertError} role="alert">
          {error}
        </div>
      ) : null}

      <div className={entStyles.postureGrid}>
        <div className={entStyles.postureMetric}>
          <p className={entStyles.postureMetricLabel}>{t('reports.assessments.total', 'Total assessments')}</p>
          <p className={entStyles.postureMetricValue}>{loading ? '—' : summary?.totalAssessments ?? 0}</p>
        </div>
        <div className={entStyles.postureMetric}>
          <p className={entStyles.postureMetricLabel}>{t('assets.scans.status', 'Completed')}</p>
          <p className={entStyles.postureMetricValue}>{loading ? '—' : summary?.completed ?? 0}</p>
        </div>
        <div className={entStyles.postureMetric}>
          <p className={entStyles.postureMetricLabel}>{t('reports.scannerRunning', 'Running')}</p>
          <p className={entStyles.postureMetricValue}>{loading ? '—' : summary?.running ?? 0}</p>
        </div>
        <div className={entStyles.postureMetric}>
          <p className={entStyles.postureMetricLabel}>{t('common.failed', 'Failed')}</p>
          <p className={entStyles.postureMetricValue}>{loading ? '—' : summary?.failed ?? 0}</p>
        </div>
        <div className={entStyles.postureMetric}>
          <p className={entStyles.postureMetricLabel}>{t('reports.assessments.targets', 'Targets assessed')}</p>
          <p className={entStyles.postureMetricValue}>{loading ? '—' : summary?.targetsAssessed ?? 0}</p>
        </div>
        <div className={entStyles.postureMetric}>
          <p className={entStyles.postureMetricLabel}>{t('reports.assessments.withFindings', 'With findings')}</p>
          <p className={entStyles.postureMetricValue}>{loading ? '—' : summary?.assessmentsWithFindings ?? 0}</p>
        </div>
        <div className={entStyles.postureMetric}>
          <p className={entStyles.postureMetricLabel}>{t('reports.assessments.zeroFindings', 'Zero-finding')}</p>
          <p className={entStyles.postureMetricValue}>{loading ? '—' : summary?.zeroFindingAssessments ?? 0}</p>
        </div>
        <div className={entStyles.postureMetric}>
          <p className={entStyles.postureMetricLabel}>{t('reports.assessments.latest', 'Latest assessment')}</p>
          <p className={entStyles.postureMetricValue} style={{ fontSize: 14 }}>
            {loading ? '—' : formatDay(summary?.latestAssessmentAt)}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Select
          className="text-sm"
          minWidth="10rem"
          variant="outline"
          options={[
            { value: 'all', label: t('reports.filters.allScanners', 'All scanners') },
            { value: 'GREENBONE', label: 'Greenbone' },
            { value: 'OWASP_ZAP', label: 'OWASP ZAP' },
            { value: 'NUCLEI', label: 'Nuclei' },
          ]}
          value={providerFilter}
          onChange={(v) => v != null && setProviderFilter(v)}
        />
        <Select
          className="text-sm"
          minWidth="10rem"
          variant="outline"
          options={[
            { value: 'all', label: t('reports.filters.allStatuses', 'All statuses') },
            { value: 'COMPLETED', label: t('assets.scans.status', 'Completed') },
            { value: 'RUNNING', label: t('reports.scannerRunning', 'Running') },
            { value: 'FAILED', label: t('common.failed', 'Failed') },
            { value: 'QUEUED', label: t('reports.scannerQueued', 'Queued') },
          ]}
          value={statusFilter}
          onChange={(v) => v != null && setStatusFilter(v)}
        />
        <Select
          className="text-sm"
          minWidth="10rem"
          variant="outline"
          options={[
            { value: 'all', label: t('reports.filters.allFindings', 'All findings') },
            { value: 'with', label: t('reports.filters.withFindings', 'Has findings') },
            { value: 'zero', label: t('reports.filters.zeroFindings', 'Zero findings') },
          ]}
          value={findingsFilter}
          onChange={(v) => v != null && setFindingsFilter(v as 'all' | 'with' | 'zero')}
        />
        <Select
          className="text-sm"
          minWidth="11rem"
          variant="outline"
          options={[
            { value: 'all', label: t('reports.filters.allPosture', 'All posture roles') },
            { value: 'current', label: t('reports.postureCurrentSource', 'Current posture source') },
            { value: 'historical', label: t('reports.postureHistoricalSnapshot', 'Historical snapshot') },
          ]}
          value={postureFilter}
          onChange={(v) => v != null && setPostureFilter(v as 'all' | 'current' | 'historical')}
        />
        <Select
          className="text-sm"
          minWidth="8rem"
          variant="outline"
          options={[
            { value: '25', label: '25' },
            { value: '50', label: '50' },
            { value: '100', label: '100' },
          ]}
          value={String(pageSize)}
          onChange={(v) => v != null && setPageSize(Number(v))}
        />
        <Button type="button" size="small" color="outline" onClick={() => void load()}>
          {t('common.refresh', 'Refresh')}
        </Button>
      </div>

      <div className={styles.enterpriseTableWrap}>
        <table className={styles.enterpriseTable}>
          <thead>
            <tr>
              <th className={styles.enterpriseTh}>{t('reports.assessments.finished', 'Finished')}</th>
              <th className={styles.enterpriseTh}>{t('reports.scanner', 'Scanner')}</th>
              <th className={styles.enterpriseTh}>{t('reports.target', 'Target')}</th>
              <th className={styles.enterpriseTh}>{t('reports.profile', 'Profile')}</th>
              <th className={styles.enterpriseTh}>{t('reports.table.status', 'Status')}</th>
              <th className={styles.enterpriseThCenter}>{t('reports.uniqueFindings', 'Unique findings')}</th>
              <th className={styles.enterpriseThCenter}>{t('reports.affectedInstances', 'Affected instances')}</th>
              <th className={styles.enterpriseThCenter}>{t('reports.rawOccurrences', 'Raw occurrences')}</th>
              <th className={styles.enterpriseThCenter}>{t('reports.newFindings', 'New')}</th>
              <th className={styles.enterpriseThCenter}>{t('reports.resolvedFindings', 'Resolved')}</th>
              <th className={styles.enterpriseTh}>{t('reports.table.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className={styles.enterpriseTdMuted} colSpan={11}>
                  {t('common.loading', 'Loading…')}
                </td>
              </tr>
            ) : pageItems.length === 0 ? (
              <tr>
                <td className={styles.enterpriseTdMuted} colSpan={11}>
                  {t('reports.assessments.empty', 'No scanner assessments match the filters')}
                </td>
              </tr>
            ) : (
              pageItems.map((row) => (
                <tr key={row.id}>
                  <td className={styles.enterpriseTdMuted} title={row.shortId || row.id}>
                    {formatDay(row.finishedAt)}
                  </td>
                  <td className={styles.enterpriseTd}>
                    {formatScannerSourceLabel(row.providerType || '')}
                    {row.governsCurrentPosture ? (
                      <span className={styles.enterpriseReportMeta}>
                        {' '}
                        · {t('reports.postureCurrentSource', 'Current posture source')}
                      </span>
                    ) : null}
                  </td>
                  <td className={styles.enterpriseTdMuted}>{row.target || '—'}</td>
                  <td className={styles.enterpriseTdMuted}>{row.profile || '—'}</td>
                  <td className={styles.enterpriseTd}>
                    <span className={statusBadgeClass(row.status || '')}>{row.status || '—'}</span>
                  </td>
                  <td className={styles.enterpriseTdCenter}>{row.uniqueFindings ?? 0}</td>
                  <td className={styles.enterpriseTdCenter}>{row.affectedInstances ?? 0}</td>
                  <td className={styles.enterpriseTdCenter}>{row.rawOccurrences ?? 0}</td>
                  <td className={styles.enterpriseTdCenter}>{row.newFindings ?? 0}</td>
                  <td className={styles.enterpriseTdCenter}>{row.resolvedFindings ?? 0}</td>
                  <td className={styles.enterpriseTd}>
                    <div className="flex flex-wrap gap-1">
                      <Button
                        type="button"
                        size="small"
                        color="outline"
                        onClick={() => void openParsed(row.scannerScanId || row.id)}
                      >
                        {t('reports.assessments.view', 'View assessment')}
                      </Button>
                      {row.previousScannerScanId ? (
                        <Button
                          type="button"
                          size="small"
                          color="outline"
                          title={
                            row.compareDelta?.comparable
                              ? t(
                                  'reports.assessments.compareHint',
                                  'New {new} · Resolved {resolved}',
                                  {
                                    new: String(row.compareDelta.newFindings ?? 0),
                                    resolved: String(row.compareDelta.resolvedFindings ?? 0),
                                  },
                                )
                              : t('reports.assessments.compareIncompatible', 'Previous scan is not compatible')
                          }
                          onClick={() => {
                            if (row.compareDelta && !row.compareDelta.comparable) {
                              setError(
                                t(
                                  'reports.assessments.compareIncompatible',
                                  'Cannot compare incompatible assessments (provider/target/profile must match).',
                                ),
                              )
                              return
                            }
                            void openParsed(row.scannerScanId || row.id)
                          }}
                        >
                          {t('reports.assessments.compare', 'Compare with previous')}
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 ? (
        <div className={styles.enterprisePagination}>
          <span>
            {t('reports.pagination.page', 'Page')} {page} {t('reports.pagination.of', 'of')} {totalPages}
            {' · '}
            {data?.total ?? 0} {t('reports.assessments.total', 'Total assessments')}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              size="small"
              color="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t('common.previous', 'Previous')}
            </Button>
            <Button
              type="button"
              size="small"
              color="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {t('common.next', 'Next')}
            </Button>
          </div>
        </div>
      ) : null}
    </ReportsEnterpriseSection>
  )
}
